import {
  cn,
  InfoPopoverProps,
  RiArrowRightDownLine,
  RiArrowRightLine,
  RiArrowRightUpLine,
  RiCompass4Line,
} from 'component-library';
import React, { HTMLAttributes, useMemo } from 'react';

import { Fact, FactType } from '@/api/rest/resources/conservation';
import {
  PlotReportFactElement,
  POTENTIAL_FACT_TYPES,
  R1FactType,
  SIGNED_FACT_TYPES,
} from '@/api/rest/resources/types/fact';
import { useFact } from '@/hooks/useFact';
import { useScreenSize } from '@/hooks/useScreenSize';
import { isNullish } from '@/utils/booleans';

import {
  Capsule,
  CapsuleContent,
  CapsuleContentProps,
  CapsuleIcon,
  CapsuleIconProps,
  CapsuleLabel,
} from '../Capsule/Capsule';

export type NCFactCapsuleProps = HTMLAttributes<HTMLDivElement> & {
  capsuleIconProps?: CapsuleIconProps;
  label: React.ReactNode;
  fact?: PlotReportFactElement;
  infoPopoverProps?: InfoPopoverProps | null;
  trend?: CapsuleContentProps['trend'];
};

export const NCFactCapsule: React.FC<NCFactCapsuleProps> = ({
  capsuleIconProps,
  label,
  fact,
  trend,
  infoPopoverProps,
  className,
}) => {
  const isSmallScreen = useScreenSize() === 'small';
  const infoPopoverPropsDelegated = useMemo(() => {
    if (isSmallScreen) return null;

    // TODO: MVP-3284 Update Capsule's styling to use text-xs instead of passing it as an override here
    return {
      ...infoPopoverProps,
      popoverTriggerProps: {
        ...infoPopoverProps?.popoverTriggerProps,
        className: cn('text-xs', infoPopoverProps?.popoverTriggerProps?.className),
      },
    } as InfoPopoverProps;
  }, [isSmallScreen, infoPopoverProps]);

  return (
    <Capsule
      label={<CapsuleLabel infoPopoverProps={infoPopoverPropsDelegated}>{label}</CapsuleLabel>}
      content={<ValueFromFact fact={fact} trend={trend} className={className} />}
      thumbnail={<CapsuleIcon {...capsuleIconProps} />}
    />
  );
};

type ValueFromFactProps = HTMLAttributes<HTMLDivElement> & {
  fact?: PlotReportFactElement;
  trend?: CapsuleContentProps['trend'];
};

export const ValueFromFact: React.FC<ValueFromFactProps> = ({ fact, trend, className }) => {
  const { value, display } = useFact(fact);

  if (isNullish(value)) {
    return <CapsuleContent className={cn('text-text-disabled', className)}>-</CapsuleContent>;
  }

  return (
    <CapsuleContent className={className} trend={trend}>
      {display}
    </CapsuleContent>
  );
};

/**
 * value      | change  |  signed fact
 * null           +            y         = right straight arrow
 * null           -            y         = right straight arrow
 * null          +/-           n         = compass
 *
 * exists         +            y         = right up arrow
 * exists         -            y         = right down arrow
 * exists        +/-           n         = compass
 *
 * GHG null      +/-          y/n        = thunderbolt
 * GHG exists    +/-          y/n        = thunderbolt
 *
 * @param fact
 * @returns Icon
 */
export const getNCChangeIcon = (fact: PlotReportFactElement | Fact<FactType> | undefined) => {
  if (!fact) {
    return <RiCompass4Line />;
  }

  if (!fact.value) {
    if ([...SIGNED_FACT_TYPES, ...POTENTIAL_FACT_TYPES].includes(fact.name as R1FactType)) return <RiArrowRightLine />;
    return <RiCompass4Line />;
  }

  if ([...SIGNED_FACT_TYPES, ...POTENTIAL_FACT_TYPES].includes(fact.name as R1FactType)) {
    if ((fact.value as number) === 0) return <RiArrowRightLine />;
    if ((fact.value as number) > 0) return <RiArrowRightUpLine />;
    return <RiArrowRightDownLine />;
  }

  return <RiCompass4Line />;
};

export const getNCChangeIconVariant = (fact: PlotReportFactElement | undefined): CapsuleIconProps['variant'] => {
  if (!fact || !fact.value) {
    return 'disabled';
  }

  if (typeof fact.value === 'number' && fact.value < 0) {
    return 'negative';
  }

  if (POTENTIAL_FACT_TYPES.includes(fact.name as R1FactType)) {
    return 'contained';
  }

  return 'default';
};
